import { useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { home, type BlogEntry } from "../content/home";
import { cn } from "../lib/cn";
import { WAVE, line } from "./SoundWaveEdge";

const BASE = __BASE_PATH__.replace(/\/+$/, "");
const { label, headline } = home.journal;
const TILT = ["-rotate-[0.5deg]", "rotate-[0.5deg]"];

const focusRing =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 focus-visible:ring-offset-paper-white";

// Until a post has its own picture, its card draws one: grid paper, the topic in the serif, and the hero's voice
// line along the bottom as the signature. The title sits under the picture, so the cover does not repeat it.
function Cover({ eyebrow }: Pick<BlogEntry, "eyebrow">) {
  return (
    <div
      data-testid="blog-cover"
      aria-hidden="true"
      className="flex h-full flex-col justify-between bg-paper bg-grid-paper bg-grid p-5"
    >
      <img src={`${BASE}/mark.svg`} alt="" width={22} height={22} className="h-[22px] w-[22px]" />
      <span className="font-serif text-[clamp(1.9rem,3vw,2.4rem)] leading-[1.02] text-char">{eyebrow}</span>
      <svg viewBox={`0 0 ${WAVE.width} ${WAVE.height}`} preserveAspectRatio="none" className="-mx-5 -mb-2 h-8 w-[calc(100%+2.5rem)] max-w-none">
        <path
          d={line}
          fill="none"
          className="stroke-brand-500/80"
          strokeWidth="1.5"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
    </div>
  );
}

// A row of post cards that scrolls sideways. The arrows appear only once there are more posts than fit.
export function JournalSection({ entries = home.journal.entries }: { entries?: BlogEntry[] }) {
  const row = useRef<HTMLDivElement>(null);
  const [overflows, setOverflows] = useState(false);

  useEffect(() => {
    const measure = () => setOverflows(!!row.current && row.current.scrollWidth > row.current.clientWidth);
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [entries]);

  const step = (direction: 1 | -1) =>
    row.current?.scrollBy({ left: direction * row.current.clientWidth * 0.8, behavior: "smooth" });

  const arrow =
    "inline-flex h-11 w-11 items-center justify-center rounded-full border border-char bg-paper-raised text-char shadow-offset-sm transition-[transform,box-shadow] duration-150 hover:-translate-x-px hover:translate-y-px active:-translate-x-[3px] active:translate-y-[4px] active:shadow-none";

  return (
    <section id="journal" aria-label={label} className="scroll-mt-20 px-4 py-24 sm:px-6 lg:py-28">
      <div className="mx-auto max-w-6xl">
        <h2 className="text-center font-serif text-[clamp(2.25rem,4.5vw,3.5rem)] font-medium leading-[1.02] tracking-[-0.015em] text-char">
          {headline.line1} <em className="text-brand-500">{headline.line2}</em>
        </h2>

        <div className="mt-12 flex justify-center">
          <div ref={row} className="max-w-full snap-x overflow-x-auto px-2 pb-4 pt-2 [scrollbar-width:none]">
            <div className="flex w-max gap-6">
              {entries.map((entry, i) => (
                <a
                  key={entry.href}
                  href={entry.href}
                  className={cn(
                    "group flex w-[80vw] max-w-[380px] shrink-0 snap-start flex-col rounded-[22px] border border-char bg-paper-raised p-3 shadow-offset-sm transition-transform duration-150 hover:rotate-0",
                    TILT[i % TILT.length],
                    focusRing,
                  )}
                >
                  {/* Same shape as the blog covers (1200x630, the link-preview size), so nothing is cropped. */}
                  <div className="aspect-[1200/630] overflow-hidden rounded-[14px] border border-char/15">
                    {entry.image ? (
                      <img src={entry.image} alt="" loading="lazy" className="h-full w-full object-cover" />
                    ) : (
                      <Cover eyebrow={entry.eyebrow} />
                    )}
                  </div>
                  <div className="px-2 pb-2 pt-4">
                    <span className="block font-serif text-2xl leading-tight text-char">{entry.title}</span>
                    <span className="mt-2 line-clamp-2 font-sans text-sm leading-relaxed text-char/70">
                      {entry.summary}
                    </span>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div>

        {overflows && (
          <div className="mt-6 flex justify-end gap-3">
            <button type="button" aria-label="Previous posts" onClick={() => step(-1)} className={cn(arrow, focusRing)}>
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            </button>
            <button type="button" aria-label="Next posts" onClick={() => step(1)} className={cn(arrow, focusRing)}>
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
